// 참여 완료 후 이동(2026-10-27, 모델 개편 2026-10-29) — 관리자 화면 1차 검증과 고객사 안내용 예시 링크.
// 🔑 규칙은 백엔드 `libs/CompleteRedirectPolicy.kt` 가 최종이다. 여기는 저장 전에 같은 사유를 먼저 보여주는 거울이고,
//    여기서 통과해도 서버가 거부하면 fetch.js 가 400 사유를 alert 한다. 두 곳의 규칙이 어긋나면 백엔드를 따른다.

export const QUERY_PARAM = "redirect_url";
export const MAX_URL_LENGTH = 1000;
export const MIN_DELAY_SEC = 2;
export const MAX_DELAY_SEC = 60;
export const DEFAULT_DELAY_SEC = 3;
export const BUTTON_NAME_MAX_LENGTH = 14;
export const MAX_ALLOWED_HOSTS = 10;

// 백엔드 Project.ProjectSettings.CompleteRedirect 와 필드 이름이 같다.
export const DEFAULT_SETTINGS = Object.freeze({
    used: false,
    buttonName: "",
    url: "",
    allowedHosts: [],
    allowQueryParam: false,
    autoRedirect: false,
    delaySec: DEFAULT_DELAY_SEC,
    showOnBenefitPage: false,
    benefitButtonName: "",
});

const HOST_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const URL_REGEX = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([/?#][\s\S]*)?$/;

/** 주소를 통째로 붙여넣어도 호스트만 남긴다. 호스트 모양이 아니면 null. (백엔드 normalizeHost 와 같은 규칙) */
export const normalizeHost = (raw) => {
    if (raw == null) return null;
    let host = String(raw).trim().toLowerCase();
    if (host === "") return null;
    if (host.includes("://")) host = host.slice(host.indexOf("://") + 3);
    host = host.split("/")[0].split("?")[0].split("#")[0];
    if (host.includes("@")) host = host.slice(host.lastIndexOf("@") + 1);
    host = host.split(":")[0];
    return HOST_REGEX.test(host) ? host : null;
};

/** 이동 URL 거부 사유(문장) 또는 null. */
export const rejectReason = (url, allowedHosts) => {
    if (url == null || String(url).trim() === "") return "이동 URL 이 비어 있습니다.";
    const value = String(url);
    if (value.length > MAX_URL_LENGTH) return `이동 URL 은 ${MAX_URL_LENGTH}자 이내여야 합니다.`;
    const hasBlankOrControl = [...value].some((ch) => /\s/.test(ch) || ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127);
    if (hasBlankOrControl) return "이동 URL 에 공백이나 제어 문자가 들어 있습니다.";
    const match = URL_REGEX.exec(value);
    if (!match) return "이동 URL 형식이 올바르지 않습니다. (예: https://www.example.com/page?x=1)";
    const [, scheme, authority] = match;
    if (scheme.toLowerCase() !== "https") return "이동 URL 은 https:// 로 시작해야 합니다.";
    if (authority.includes("@")) return "이동 URL 에 인증 정보(@)를 넣을 수 없습니다.";
    const host = normalizeHost(authority);
    if (!host) return "이동 URL 의 도메인을 읽을 수 없습니다.";
    const allowed = (allowedHosts || []).map(normalizeHost).filter(Boolean);
    if (!allowed.includes(host)) return `허용 도메인에 없는 주소입니다. (도메인: ${host})`;
    return null;
};

/**
 * 저장 전 검증. 문제가 없으면 {ok: true, settings: 다듬은 값}, 있으면 {ok: false, message}.
 * 사용 시 필수 = 허용 도메인 · 버튼명 · 디폴트 이동 URL. 자동 이동이 켜진 경우만 대기 시간을 검사한다.
 * '사용 안 함' 이면 규칙을 따지지 않는다(백엔드와 동일).
 */
export const validateSettings = (input) => {
    const s = {...DEFAULT_SETTINGS, ...(input || {})};
    const hostsRaw = (s.allowedHosts || []).map((h) => String(h ?? "").trim()).filter((h) => h !== "");
    const hosts = [];
    for (const raw of hostsRaw) {
        const host = normalizeHost(raw);
        if (!host && s.used) return {ok: false, message: `허용 도메인 형식이 올바르지 않습니다: "${raw}" (예: www.example.com)`};
        const value = host ?? raw.toLowerCase();
        if (!hosts.includes(value)) hosts.push(value);
    }
    const url = String(s.url ?? "").trim();
    const buttonName = String(s.buttonName ?? "").trim();
    const benefitButtonName = String(s.benefitButtonName ?? "").trim();
    const delaySec = Number(s.delaySec);

    if (s.used) {
        if (hosts.length === 0) return {ok: false, message: "허용 도메인을 1개 이상 등록해 주세요. 등록한 도메인으로만 이동합니다."};
        if (hosts.length > MAX_ALLOWED_HOSTS) return {ok: false, message: `허용 도메인은 최대 ${MAX_ALLOWED_HOSTS}개까지 등록할 수 있습니다.`};
        if (buttonName === "") return {ok: false, message: "버튼명을 입력해 주세요. (결과 화면에 표시되는 이동 버튼의 이름)"};
        if (buttonName.length > BUTTON_NAME_MAX_LENGTH) return {ok: false, message: `버튼명은 ${BUTTON_NAME_MAX_LENGTH}자 이내여야 합니다.`};
        if (url === "") return {ok: false, message: "디폴트 이동 URL 을 입력해 주세요. 진입 URL 파라미터가 없거나 거부될 때 이 주소로 이동합니다."};
        const reason = rejectReason(url, hosts);
        if (reason) return {ok: false, message: reason};
        if (s.autoRedirect && (!Number.isInteger(delaySec) || delaySec < MIN_DELAY_SEC || delaySec > MAX_DELAY_SEC)) {
            return {ok: false, message: `자동 이동 대기 시간은 ${MIN_DELAY_SEC}초 이상 ${MAX_DELAY_SEC}초 이하의 정수여야 합니다.`};
        }
        if (benefitButtonName.length > BUTTON_NAME_MAX_LENGTH) return {ok: false, message: `쿠폰 화면 버튼명은 ${BUTTON_NAME_MAX_LENGTH}자 이내여야 합니다.`};
    }

    return {
        ok: true,
        settings: {
            used: !!s.used,
            buttonName: buttonName === "" ? null : buttonName,
            url: url === "" ? null : url,
            allowedHosts: hosts,
            allowQueryParam: !!s.allowQueryParam,
            autoRedirect: !!s.autoRedirect,
            delaySec: Number.isInteger(delaySec) ? delaySec : DEFAULT_DELAY_SEC,
            showOnBenefitPage: !!s.showOnBenefitPage,
            benefitButtonName: benefitButtonName === "" ? null : benefitButtonName,
        },
    };
};

/**
 * 고객사 안내용 예시 링크. 진입 주소 뒤에 `?redirect_url=<encodeURIComponent(이동 주소)>` 를 붙인다.
 * 🔴 encodeURIComponent 한 번 — 고객사에 전달하는 규칙("값 전체를 한 번만 인코딩")과 같은 결과여야 한다.
 */
export const buildExampleLink = (entryUrl, targetUrl) => {
    const entry = String(entryUrl || "https://omopl.com/sh/xxxxxxxx").trim();
    const target = String(targetUrl || "").trim();
    if (target === "") return entry;
    return `${entry}${entry.includes("?") ? "&" : "?"}${QUERY_PARAM}=${encodeURIComponent(target)}`;
};
